import React, { useCallback, useRef } from "react";
import { Mic, Square } from "lucide-react";
import { useClara } from "../contexts/ClaraContext";
import useSpeechRecognition from "../hooks/useSpeechRecognition";

/**
 * VoiceInput — accessible text input/textarea with voice prompt + dictation.
 *
 * Why: For fully-blind users, every typeable field must announce itself on
 * focus and provide a microphone affordance to dictate.
 *
 * Props:
 *   id           string  required for the <label htmlFor>
 *   value        string  controlled value
 *   onChange     (next:string) => void
 *   fieldLabel   string  spoken name of the field (e.g. "contact's first name")
 *   placeholder  string  visual placeholder
 *   as           "input" | "textarea"   default "input"
 *   rows         number  textarea rows (default 4)
 *   inputClassName string  styling for the underlying element
 *   testId       string  data-testid for the input (mic gets `${testId}-mic`)
 *   promptText   string  optional override for the focus announcement
 *   autoFocusPrompt bool  default true
 *   inputProps   object  passthrough props on the input (type, required, etc.)
 */
export const VoiceInput = ({
    id,
    value,
    onChange,
    fieldLabel,
    placeholder = "",
    as = "input",
    rows = 4,
    inputClassName = "",
    testId,
    promptText,
    autoFocusPrompt = true,
    inputProps = {},
}) => {
    const { speak } = useClara();
    const sr = useSpeechRecognition();
    const announcedRef = useRef(false);

    const message =
        promptText ||
        `${fieldLabel ? fieldLabel + ". " : ""}Type your answer, or tap the microphone to speak.`;

    const handleFocus = () => {
        if (!autoFocusPrompt) return;
        if (announcedRef.current) return;
        announcedRef.current = true;
        speak(message);
    };
    const handleBlur = () => {
        // re-arm on next focus
        announcedRef.current = false;
    };

    const handleMicTap = useCallback(() => {
        if (sr.isListening) {
            sr.stop();
            return;
        }
        if (!sr.supported) {
            speak(
                "Voice input isn't available on this device. Please type your answer.",
            );
            return;
        }
        speak(`Listening for ${fieldLabel || "your input"}. Speak now.`, {
            onEnd: () => {
                sr.start((text, err) => {
                    if (err) {
                        speak("I didn't catch that. Try again, or type.");
                        return;
                    }
                    if (text) {
                        onChange(text);
                        speak(`Got it: ${text}.`);
                    }
                });
            },
        });
    }, [sr, speak, fieldLabel, onChange]);

    const baseTestId = testId || `voice-input-${id || "field"}`;

    const sharedProps = {
        id,
        value,
        onChange: (e) => onChange(e.target.value),
        onFocus: handleFocus,
        onBlur: handleBlur,
        placeholder,
        "data-testid": baseTestId,
        className: inputClassName,
        ...inputProps,
    };

    return (
        <div className="relative">
            {as === "textarea" ? (
                <textarea rows={rows} {...sharedProps} />
            ) : (
                <input type="text" {...sharedProps} />
            )}
            <button
                type="button"
                onClick={handleMicTap}
                aria-label={`${sr.isListening ? "Stop dictating" : "Dictate"} ${fieldLabel || "input"}`}
                aria-pressed={sr.isListening}
                data-testid={`${baseTestId}-mic`}
                className={`absolute right-2 ${as === "textarea" ? "top-2" : "top-1/2 -translate-y-1/2"} w-10 h-10 rounded-xl flex items-center justify-center transition-colors shadow-sm ${
                    sr.isListening
                        ? "bg-[#B8860B] text-[#1A1A2E] animate-pulse"
                        : "bg-[#FFF8F0] text-[#0A3D7A] hover:bg-[#0A3D7A] hover:text-white border border-[#0A3D7A]/15"
                }`}
            >
                {sr.isListening ? (
                    <Square className="w-4 h-4" aria-hidden="true" />
                ) : (
                    <Mic className="w-5 h-5" aria-hidden="true" />
                )}
            </button>
        </div>
    );
};

export default VoiceInput;
